import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DwComponent, DwComponentType, DwEventService, DwMediaRepositoryService, DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';

import { AppMetaData, DwUserEntity } from '../../../../meta-data/app-meta-data.service';

@DwComponent({
  key: 'securityAvatar',
  componentType: DwComponentType.pageComponent,
  name: 'Aviator Avatar'
})
@Component({
  selector: 'app-user-avatar',
  templateUrl: './usre-avatar.component.html',
  styleUrls: ['./usre-avatar.component.scss']
})
export class UsreAvatarComponent implements OnInit, OnDestroy {
  initials: string = '';
  imageUrl: string;

  private readonly subscriptions = new Subscription();
  private lastAvatarMediaId?: number;
  private loadingAvatarMediaId?: number;
  private loadedProfileUserId?: number;
  private loadingProfileUserId?: number;

  constructor(
    private dwSecurityUserService: DwSecurityUserService,
    private dwMediaRepositoryService: DwMediaRepositoryService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    private dwEventService: DwEventService
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.dwSecurityUserService.securityContextChanged().subscribe(() => {
      this.loadedProfileUserId = undefined;
      this.loadingProfileUserId = undefined;
      this.setupUserAvatar();
    }));

    this.subscriptions.add(this.dwEventService.getEventData(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwUser.ItemDetail.itemName), false).subscribe(eventData => {
      this.handleUserChanged(eventData);
    }));

    this.setupUserAvatar();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setupUserAvatar(): void {
    const user = this.dwSecurityUserService?.securityContext?.ApplicationUser;

    if (!user || user?.UserId == 0) {
      this.initials = '';
      this.imageUrl = '';
      this.lastAvatarMediaId = undefined;
      this.loadedProfileUserId = undefined;
      this.loadingProfileUserId = undefined;
      return;
    }

    this.initials = this.getUserInitials(user);
    const hasAvatarSource = this.setupUserImage(user);

    if (!hasAvatarSource) {
      this.loadCurrentUserProfileAvatar(user.UserId);
    }
  }

  clearImage(): void {
    this.imageUrl = '';
  }

  private setupUserImage(user: any): boolean {
    const mediaPath = this.getUserMediaPath(user);

    if (mediaPath) {
      this.imageUrl = this.dwMediaRepositoryService.getImageUrlFromMediaPath(mediaPath, 40, 40);
      this.lastAvatarMediaId = user?.UserPictureMediaId;
      return true;
    }

    const avatarMediaId = user?.UserPictureMediaId;

    if (!avatarMediaId) {
      this.imageUrl = '';
      this.lastAvatarMediaId = undefined;
      this.loadingAvatarMediaId = undefined;
      return false;
    }

    if (avatarMediaId === this.lastAvatarMediaId && (this.imageUrl || this.loadingAvatarMediaId === avatarMediaId)) {
      return true;
    }

    this.imageUrl = '';
    this.lastAvatarMediaId = avatarMediaId;
    this.loadingAvatarMediaId = avatarMediaId;

    this.dwMediaRepositoryService.loadMedia(avatarMediaId).subscribe(mediaItems => {
      if (this.loadingAvatarMediaId === avatarMediaId) {
        this.loadingAvatarMediaId = undefined;
      }

      const mediaItem = (mediaItems || []).find(media => media?.MediaId === avatarMediaId);

      if (!mediaItem?.MediaPath || this.lastAvatarMediaId !== avatarMediaId) {
        return;
      }

      this.imageUrl = this.dwMediaRepositoryService.getImageUrlFromMediaPath(mediaItem.MediaPath, 40, 40);
    }, () => {
      if (this.loadingAvatarMediaId === avatarMediaId) {
        this.loadingAvatarMediaId = undefined;
      }
    });

    return true;
  }

  private loadCurrentUserProfileAvatar(userId: number, force: boolean = false): void {
    if (!userId) {
      return;
    }

    if (!force && (this.loadingProfileUserId === userId || this.loadedProfileUserId === userId)) {
      return;
    }

    this.loadingProfileUserId = userId;

    const query = AppMetaData.DwUser.CreateQueryBuilder();
    query.addFilterAnd(filter => filter.Equal(attribute => attribute.UserId, userId));

    this.dwOrmDataService.executeQuerySingle<DwUserEntity>(query)
      .subscribe(profileUser => {
        if (this.loadingProfileUserId === userId) {
          this.loadingProfileUserId = undefined;
        }

        this.loadedProfileUserId = userId;

        if (!profileUser || profileUser.UserId !== this.getCurrentUserId()) {
          return;
        }

        this.setupUserImage(profileUser);
      }, () => {
        if (this.loadingProfileUserId === userId) {
          this.loadingProfileUserId = undefined;
        }
      });
  }

  private handleUserChanged(eventData: any): void {
    const currentUserId = this.getCurrentUserId();

    if (!currentUserId) {
      return;
    }

    const changedUser = eventData?.newEntity || eventData?.newItem || eventData;
    const changedUserId = changedUser?.UserId;

    if (changedUserId && changedUserId !== currentUserId) {
      return;
    }

    this.loadedProfileUserId = undefined;
    this.loadCurrentUserProfileAvatar(currentUserId, true);
  }

  private getUserMediaPath(user: any): string {
    return user?.UserPictureDwMedia?.MediaPath || user?.UserPictureMediaPath || user?.DwroMediaMediaPath || '';
  }

  private getCurrentUserId(): number {
    return this.dwSecurityUserService?.securityContext?.ApplicationUser?.UserId;
  }

  private getUserInitials(user: any): string {
    const firstInitial = user?.FirstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = user?.LastName?.charAt(0)?.toUpperCase() || '';

    return `${firstInitial}${lastInitial}`;
  }
}
